/**
 * 금액 글자 다루기 — <b>쉼표를 붙이고 걷어 낸다</b>.
 *
 * <p>여기 두는 이유: 같은 일을 하는 함수가 결재 서식(`formSchema.ts`)과 현장 표에 따로 생겼다
 * (2026-09-15). 쉼표 규칙이 화면마다 갈리면 「여기서는 0.5 가 되고 저기서는 0,5 가 된다」 가
 * 언젠가 반드시 생긴다. 셈이 아니라 <b>글자 규칙</b>이라 도메인 폴더가 아니라 lib 에 둔다.
 */

/**
 * 천 단위 쉼표 (사용자 지시 2026-09-15 · 「교육비 금액이 , 안 들어가서 보기 불편함」).
 *
 * <p><b>금액 칸에만</b> 건다. 값 전체를 훑어 숫자를 꾸미면 협조전 비고에 적은 「2026」이
 * 「2,026」이 되고, 사람이 적은 글이 화면에서 달라진다.
 *
 * <p>소수점 아래는 건드리지 않는다 — 반차 「0.5」 와 억원 눈금의 「13.2」 가 이 칸을 지나간다.
 * 치는 도중의 「1234.」 도 그대로 남겨야 다음 글자를 이어 칠 수 있다.
 */
export function groupDigits(raw: string): string {
  if (raw === '') return '';
  const minus = raw.trim().startsWith('-') ? '-' : '';
  const [head = '', ...tail] = raw.replace(/[^\d.]/g, '').split('.');
  const grouped = head.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return minus + grouped + (tail.length > 0 ? `.${tail.join('')}` : '');
}

/**
 * 쉼표를 걷어 <b>저장할 값</b>으로 되돌린다. 상태에는 언제나 쉼표 없는 글자가 들어가야 한다 —
 * 저장할 때 {@code Number()} 가 읽는데, 쉼표가 섞이면 {@code NaN} 이 되어 금액이 통째로 비어 버린다.
 */
export function ungroupDigits(text: string): string {
  return text.replace(/[^\d.-]/g, '');
}

/** 쉼표를 붙여도 되는 글자인가 — 숫자·쉼표·점·앞의 마이너스뿐이다. */
const PLAIN_NUMBER = /^-?[\d,]*\.?\d*$/;

/**
 * <b>커서를 지키며</b> 그 자리에서 쉼표를 다시 붙인다 — 값을 리액트가 들지 않는
 * 입력칸(공무 표·설정 카드처럼 blur 에 커밋하는 칸)을 위한 것이다.
 *
 * <p>{@code onInput} 에서 부른다. 값을 직접 고쳐 쓰므로 부르는 쪽의 커밋 규약
 * ({@code onBlur} 의 {@code parseAmount})은 그대로 둔다 — 그쪽은 쉼표를 걷어 내고 읽는다.
 *
 * <p><b>「824억」·「3천만」 은 건드리지 않는다.</b> 그 칸들은 단위를 말로 받는데, 쉼표를 붙이려고
 * 숫자만 남기면 사람이 친 「억」 이 사라지고 값이 1억분의 1이 된다.
 */
export function regroupInPlace(input: HTMLInputElement): void {
  const raw = input.value;
  if (!PLAIN_NUMBER.test(raw)) return;

  const caret = input.selectionStart ?? raw.length;
  const digitsBefore = raw.slice(0, caret).replace(/[^\d]/g, '').length;
  const next = groupDigits(raw);
  if (next === raw) return;

  input.value = next;
  let at = 0;
  let seen = 0;
  while (at < next.length && seen < digitsBefore) {
    if (/\d/.test(next[at]!)) seen += 1;
    at += 1;
  }
  input.setSelectionRange(at, at);
}
